import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../../Components/Common/HelpArticleLink";
import SupportAttachmentList from "../../../../../Components/Support/SupportAttachmentList";
import SupportTicketModal from "../../../../../Components/Support/SupportTicketModal";
import { HELP_ARTICLE_SUPPORT_TICKETS } from "../../../../../helpers/helpCenter";
import {
  clearSupportReplyDraft,
  dispatchSupportSummaryRefresh,
  hasSupportReplyDraft,
  hasUnseenAgentMessage,
  loadSupportReplyDraft,
  copySupportTicketLink,
  markSupportTicketSeen,
  notifySupportAgentReplyBrowser,
  playSoftNotificationBeep,
  publishSupportLiveUpdate,
  resolveSupportLivePollMs,
  resolveAgentSla,
  saveSupportReplyDraft,
  subscribeSupportLiveUpdates,
} from "../../../../../helpers/supportTicketUx";
import {
  downloadSupportAttachment,
  fetchSupportAttachmentBlob,
  getSupportTicket,
  listSupportTickets,
  replySupportTicket,
  submitSupportSatisfaction,
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketCategory,
  SupportTicketStatus,
} from "../../../../../services/supportTicketService";
import { PaginationMeta } from "../../../../../types/api";

const CATEGORIES: SupportTicketCategory[] = [
  "agenda",
  "orders",
  "payments",
  "account",
  "integration",
  "product_feedback",
  "other",
];

const STATUSES: SupportTicketStatus[] = [
  "received",
  "triaging",
  "in_progress",
  "awaiting_tenant",
  "resolved",
  "closed",
];

const parseCategory = (value: string | null): SupportTicketCategory | "" => {
  if (value && CATEGORIES.includes(value as SupportTicketCategory)) {
    return value as SupportTicketCategory;
  }

  return "";
};

const parseStatus = (value: string | null): SupportTicketStatus | "" => {
  if (value && STATUSES.includes(value as SupportTicketStatus)) {
    return value as SupportTicketStatus;
  }

  return "";
};

const statusColor = (status: SupportTicketStatus): string => {
  switch (status) {
    case "resolved":
    case "closed":
      return "success";
    case "awaiting_tenant":
      return "warning";
    case "in_progress":
    case "triaging":
      return "info";
    default:
      return "secondary";
  }
};

const SupportTicketsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const categoryFilter = parseCategory(searchParams.get("category"));
  const statusFilter = parseStatus(searchParams.get("status"));
  const searchFilter = searchParams.get("search") || "";
  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const [searchInput, setSearchInput] = useState(searchFilter);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [replying, setReplying] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [satisfactionComment, setSatisfactionComment] = useState("");
  const [savingSatisfaction, setSavingSatisfaction] = useState(false);
  const [seenVersion, setSeenVersion] = useState(0);
  const [lastPolledAgentAt, setLastPolledAgentAt] = useState<string | null>(null);
  const selectedRef = useRef<SupportTicket | null>(null);
  const lastPolledAgentAtRef = useRef<string | null>(null);

  selectedRef.current = selected;
  lastPolledAgentAtRef.current = lastPolledAgentAt;

  const hasActiveFilters = Boolean(categoryFilter || statusFilter || searchFilter);

  const updateFilters = useCallback(
    (patch: {
      category?: SupportTicketCategory | "";
      status?: SupportTicketStatus | "";
      search?: string;
      page?: number;
    }) => {
      const next = new URLSearchParams(searchParams);

      const nextCategory =
        patch.category !== undefined ? patch.category : categoryFilter;
      const nextStatus = patch.status !== undefined ? patch.status : statusFilter;
      const nextSearch =
        patch.search !== undefined ? patch.search : searchFilter;
      const nextPage = patch.page !== undefined ? patch.page : page;

      if (nextCategory) {
        next.set("category", nextCategory);
      } else {
        next.delete("category");
      }

      if (nextStatus) {
        next.set("status", nextStatus);
      } else {
        next.delete("status");
      }

      if (nextSearch.trim()) {
        next.set("search", nextSearch.trim());
      } else {
        next.delete("search");
      }

      if (nextPage > 1) {
        next.set("page", String(nextPage));
      } else {
        next.delete("page");
      }

      setSearchParams(next, { replace: true });
    },
    [categoryFilter, page, searchFilter, searchParams, setSearchParams, statusFilter]
  );

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    document.title = t("support.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    setSearchInput(searchFilter);
  }, [searchFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() === searchFilter) {
        return;
      }

      updateFilters({ search: searchInput, page: 1 });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput, searchFilter, updateFilters]);

  const loadList = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listSupportTickets({
        page,
        per_page: 15,
        category: categoryFilter,
        status: statusFilter,
        search: searchFilter || undefined,
      });

      if (response.success) {
        setTickets(response.data || []);
        setMeta(response.meta || null);
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("support.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, categoryFilter, statusFilter, searchFilter, t]);

  const loadDetail = useCallback(
    async (id: number) => {
      setDetailLoading(true);

      try {
        const response = await getSupportTicket(id);

        if (response.success) {
          setSelected(response.data);
          setSatisfactionComment("");
          setReplyBody(loadSupportReplyDraft(id));
          setReplyFiles([]);
          setLastPolledAgentAt(response.data.last_agent_message_at || null);
          markSupportTicketSeen(id);
          setSeenVersion((current) => current + 1);
          dispatchSupportSummaryRefresh();
        }
      } catch (error: unknown) {
        const apiError = error as { message?: string };
        toast.error(apiError.message || t("support.toast.loadError"));
      } finally {
        setDetailLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (ticketId) {
      void loadDetail(Number(ticketId));
    } else {
      setSelected(null);
      setLastPolledAgentAt(null);
    }
  }, [ticketId, loadDetail]);

  useEffect(() => {
    if (!ticketId) {
      return;
    }

    const ticketIdValue = Number(ticketId);
    let cancelled = false;
    let timeoutId = 0;

    const poll = async () => {
      const current = selectedRef.current;

      if (!current || current.id !== ticketIdValue) {
        return;
      }

      try {
        const detailResponse = await getSupportTicket(ticketIdValue);

        if (!detailResponse.success || !detailResponse.data) {
          return;
        }

        const nextAgentAt = detailResponse.data.last_agent_message_at || null;
        const previousAgentAt = lastPolledAgentAtRef.current;

        if (
          nextAgentAt &&
          previousAgentAt &&
          new Date(nextAgentAt).getTime() > new Date(previousAgentAt).getTime()
        ) {
          toast.info(t("support.toast.newAgentReply"), { autoClose: 4000 });
          playSoftNotificationBeep();
          notifySupportAgentReplyBrowser(
            t("support.toast.newAgentReply"),
            detailResponse.data.subject,
            ticketIdValue
          );
          publishSupportLiveUpdate({
            ticketId: ticketIdValue,
            lastAgentMessageAt: nextAgentAt,
          });
          setSelected(detailResponse.data);
          markSupportTicketSeen(ticketIdValue);
          setSeenVersion((version) => version + 1);
          void loadList();
          dispatchSupportSummaryRefresh();
        } else if (detailResponse.data.updated_at !== current.updated_at) {
          setSelected(detailResponse.data);
        }

        setLastPolledAgentAt(nextAgentAt);
      } catch {
        // silent poll
      }
    };

    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        void poll().finally(() => {
          if (!cancelled) {
            schedule();
          }
        });
      }, resolveSupportLivePollMs());
    };

    const onVisibility = () => {
      window.clearTimeout(timeoutId);
      if (document.visibilityState === "visible") {
        void poll().finally(() => {
          if (!cancelled) {
            schedule();
          }
        });
      } else {
        schedule();
      }
    };

    schedule();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ticketId, loadList, t]);

  useEffect(() => {
    return subscribeSupportLiveUpdates((payload) => {
      if (!ticketId || Number(ticketId) !== payload.ticketId) {
        return;
      }

      const previousAgentAt = lastPolledAgentAtRef.current;
      if (
        previousAgentAt &&
        new Date(payload.lastAgentMessageAt).getTime() <=
          new Date(previousAgentAt).getTime()
      ) {
        return;
      }

      void getSupportTicket(payload.ticketId).then((detailResponse) => {
        if (!detailResponse.success || !detailResponse.data) {
          return;
        }

        toast.info(t("support.toast.newAgentReply"), { autoClose: 4000 });
        playSoftNotificationBeep();
        setSelected(detailResponse.data);
        setLastPolledAgentAt(payload.lastAgentMessageAt);
        markSupportTicketSeen(payload.ticketId);
        setSeenVersion((version) => version + 1);
        void loadList();
        dispatchSupportSummaryRefresh();
      });
    });
  }, [ticketId, loadList, t]);

  const openTicket = (id: number) => {
    navigate(`/support/tickets/${id}${window.location.search}`);
  };

  const handleCloseDetail = () => {
    if (selected && hasSupportReplyDraft(selected.id)) {
      const discard = window.confirm(t("support.confirmDiscardDraft"));

      if (!discard) {
        return;
      }

      clearSupportReplyDraft(selected.id);
      setReplyBody("");
    }

    setSelected(null);
    navigate(`/support/tickets${window.location.search}`);
  };

  const handleReply = async () => {
    if (!selected || !replyBody.trim()) {
      return;
    }

    setReplying(true);

    try {
      await replySupportTicket(selected.id, replyBody.trim(), replyFiles);
      toast.success(t("support.toast.replySent"));
      clearSupportReplyDraft(selected.id);
      setReplyBody("");
      setReplyFiles([]);
      await loadDetail(selected.id);
      await loadList();
      dispatchSupportSummaryRefresh();
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("support.toast.replyError"));
    } finally {
      setReplying(false);
    }
  };

  const handleDownload = async (attachment: SupportTicketAttachment) => {
    if (!selected) {
      return;
    }

    setDownloadingId(attachment.id);

    try {
      await downloadSupportAttachment(
        selected.id,
        attachment.id,
        attachment.original_filename
      );
    } catch {
      toast.error(t("support.toast.downloadError"));
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchBlob = useCallback(
    (ticketIdValue: number, attachmentId: number) =>
      fetchSupportAttachmentBlob(ticketIdValue, attachmentId),
    []
  );

  const handleSatisfaction = async (value: "yes" | "no") => {
    if (!selected) {
      return;
    }

    setSavingSatisfaction(true);

    try {
      const response = await submitSupportSatisfaction(selected.id, {
        satisfaction: value,
        comment: value === "no" ? satisfactionComment.trim() || undefined : undefined,
      });

      if (response.success) {
        setSelected(response.data);
        toast.success(
          value === "no"
            ? t("support.toast.satisfactionReopened")
            : t("support.toast.satisfactionSaved")
        );
        await loadList();
        dispatchSupportSummaryRefresh();
      }
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError.message || t("support.toast.satisfactionError"));
    } finally {
      setSavingSatisfaction(false);
    }
  };

  const canShowCsat = useMemo(() => {
    if (!selected) {
      return false;
    }

    return (
      (selected.status === "resolved" || selected.status === "closed") &&
      !selected.satisfaction
    );
  }, [selected]);

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        <BreadCrumb
          title={t("support.breadcrumb.title")}
          pageTitle={t("support.breadcrumb.pageTitle")}
        />

        <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
          <div className="d-flex flex-wrap gap-2">
            <Button color="primary" onClick={() => setTicketModalOpen(true)}>
              {t("support.actions.openTicket")}
            </Button>
            <Button color="soft-primary" onClick={() => setFeedbackModalOpen(true)}>
              {t("support.actions.sendFeedback")}
            </Button>
          </div>
          <HelpArticleLink articlePath={HELP_ARTICLE_SUPPORT_TICKETS} />
        </div>

        <Row>
          <Col lg={selected || ticketId ? 5 : 12}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-3">{t("support.listTitle")}</h5>
                <Row className="g-2">
                  <Col xs={12}>
                    <Label for="support-filter-search" className="mb-1">
                      {t("support.filters.search")}
                    </Label>
                    <Input
                      id="support-filter-search"
                      type="search"
                      value={searchInput}
                      placeholder={t("support.filters.searchPlaceholder")}
                      onChange={(event) => setSearchInput(event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label for="support-filter-category" className="mb-1">
                      {t("support.fields.category")}
                    </Label>
                    <Input
                      id="support-filter-category"
                      type="select"
                      value={categoryFilter}
                      onChange={(event) => {
                        updateFilters({
                          category: event.target.value as SupportTicketCategory | "",
                          page: 1,
                        });
                      }}
                    >
                      <option value="">{t("support.filters.allCategories")}</option>
                      {CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {t(`support.categories.${category}`)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={6}>
                    <Label for="support-filter-status" className="mb-1">
                      {t("support.filters.status")}
                    </Label>
                    <Input
                      id="support-filter-status"
                      type="select"
                      value={statusFilter}
                      onChange={(event) => {
                        updateFilters({
                          status: event.target.value as SupportTicketStatus | "",
                          page: 1,
                        });
                      }}
                    >
                      <option value="">{t("support.filters.allStatuses")}</option>
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {t(`support.statuses.${status}`)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner />
                  </div>
                ) : tickets.length === 0 ? (
                  <div>
                    <p className="text-muted mb-2">
                      {hasActiveFilters
                        ? t("support.emptyFiltered")
                        : t("support.empty")}
                    </p>
                    {hasActiveFilters ? (
                      <Button color="soft-primary" size="sm" onClick={clearFilters}>
                        {t("support.actions.clearFilters")}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {tickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => openTicket(ticket.id)}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-medium">
                              #{ticket.public_number} — {ticket.subject}
                              {ticket.status === "awaiting_tenant" ? (
                                <Badge color="warning" className="ms-2">
                                  {t("support.badges.needsReply")}
                                </Badge>
                              ) : null}
                              {seenVersion >= 0 &&
                              hasUnseenAgentMessage(
                                ticket.id,
                                ticket.last_agent_message_at
                              ) ? (
                                <Badge color="info" className="ms-2">
                                  {t("support.badges.newMessage")}
                                </Badge>
                              ) : null}
                              {(ticket.attachments_count ?? 0) > 0 ? (
                                <Badge
                                  color="light"
                                  className="ms-2 text-muted"
                                  title={t("support.list.attachments", {
                                    count: ticket.attachments_count,
                                  })}
                                >
                                  <i className="ri-attachment-2 me-1" />
                                  {ticket.attachments_count}
                                </Badge>
                              ) : null}
                            </div>
                            <small className="text-muted">
                              {t(`support.categories.${ticket.category}`)}
                            </small>
                          </div>
                          <Badge color={statusColor(ticket.status)}>
                            {t(`support.statuses.${ticket.status}`)}
                          </Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {meta && meta.last_page > 1 ? (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <Button
                      color="light"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => updateFilters({ page: Math.max(1, page - 1) })}
                    >
                      {t("support.actions.previous")}
                    </Button>
                    <span className="text-muted small">
                      {page}/{meta.last_page}
                    </span>
                    <Button
                      color="light"
                      size="sm"
                      disabled={page >= meta.last_page}
                      onClick={() => updateFilters({ page: page + 1 })}
                    >
                      {t("support.actions.next")}
                    </Button>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>

          {selected || detailLoading ? (
            <Col lg={7}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                  <h5 className="card-title mb-0">
                    {selected
                      ? `#${selected.public_number} — ${selected.subject}`
                      : t("support.detailLoading")}
                  </h5>
                  <div className="d-flex gap-2">
                    {selected ? (
                      <Button
                        color="soft-secondary"
                        size="sm"
                        onClick={() => {
                          void copySupportTicketLink("tenant", selected.id).then(
                            (ok) => {
                              if (ok) {
                                toast.success(t("support.toast.linkCopied"));
                              } else {
                                toast.error(t("support.toast.linkCopyError"));
                              }
                            }
                          );
                        }}
                      >
                        <i className="ri-link me-1" />
                        {t("support.actions.copyLink")}
                      </Button>
                    ) : null}
                    <Button
                      color="light"
                      size="sm"
                      onClick={handleCloseDetail}
                    >
                      {t("support.actions.close")}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {detailLoading || !selected ? (
                    <div className="text-center py-4">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <Badge color={statusColor(selected.status)} className="mb-3">
                        {t(`support.statuses.${selected.status}`)}
                      </Badge>
                      {(() => {
                        const sla = resolveAgentSla(selected.last_agent_message_at);
                        if (!sla) {
                          return null;
                        }

                        const color =
                          sla.tone === "critical"
                            ? "danger"
                            : sla.tone === "warn"
                              ? "warning"
                              : "success";

                        return (
                          <p className={`small text-${color} mb-2`}>
                            <i className="ri-time-line me-1" />
                            {t("support.sla.sinceAgent", {
                              time: new Date(
                                selected.last_agent_message_at as string
                              ).toLocaleString(),
                            })}
                            {sla.tone === "critical"
                              ? ` — ${t("support.sla.critical")}`
                              : sla.tone === "warn"
                                ? ` — ${t("support.sla.warn")}`
                                : ""}
                          </p>
                        );
                      })()}
                      <p className="text-muted">{selected.description}</p>
                      <SupportAttachmentList
                        ticketId={selected.id}
                        attachments={selected.attachments}
                        fetchBlob={fetchBlob}
                        onDownload={handleDownload}
                        downloadingId={downloadingId}
                      />

                      <h6 className="mt-4">{t("support.threadTitle")}</h6>
                      <div className="d-flex flex-column gap-3 mb-4">
                        {(selected.messages || []).map((message) => (
                          <div
                            key={message.id}
                            className="border rounded p-3 bg-light"
                          >
                            <div className="d-flex justify-content-between mb-1">
                              <strong>
                                {message.author_type === "platform_agent"
                                  ? t("support.authors.agent")
                                  : message.author_type === "system"
                                    ? t("support.authors.system")
                                    : message.author_user?.name ||
                                      t("support.authors.you")}
                              </strong>
                              <small className="text-muted">
                                {message.created_at
                                  ? new Date(message.created_at).toLocaleString()
                                  : ""}
                              </small>
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{message.body}</div>
                            <SupportAttachmentList
                              ticketId={selected.id}
                              attachments={message.attachments}
                              fetchBlob={fetchBlob}
                              onDownload={handleDownload}
                              downloadingId={downloadingId}
                            />
                          </div>
                        ))}
                      </div>

                      {canShowCsat ? (
                        <div className="border rounded p-3 mb-4 bg-soft-primary">
                          <h6 className="mb-2">{t("support.csat.title")}</h6>
                          <p className="text-muted small mb-3">
                            {t("support.csat.intro")}
                          </p>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            <Button
                              color="success"
                              size="sm"
                              disabled={savingSatisfaction}
                              onClick={() => void handleSatisfaction("yes")}
                            >
                              {t("support.csat.yes")}
                            </Button>
                            <Button
                              color="soft-danger"
                              size="sm"
                              disabled={savingSatisfaction}
                              onClick={() => void handleSatisfaction("no")}
                            >
                              {t("support.csat.no")}
                            </Button>
                          </div>
                          <Label for="support-csat-comment" className="mb-1">
                            {t("support.csat.commentLabel")}
                          </Label>
                          <Input
                            id="support-csat-comment"
                            type="textarea"
                            rows={2}
                            value={satisfactionComment}
                            onChange={(event) =>
                              setSatisfactionComment(event.target.value)
                            }
                            placeholder={t("support.csat.commentPlaceholder")}
                          />
                        </div>
                      ) : null}

                      {selected.satisfaction ? (
                        <p className="text-muted small mb-3">
                          {selected.satisfaction === "yes"
                            ? t("support.csat.thanksYes")
                            : t("support.csat.thanksNo")}
                        </p>
                      ) : null}

                      {selected.status === "awaiting_tenant" ? (
                        <div>
                          <Label for="support-reply">{t("support.fields.reply")}</Label>
                          <Input
                            id="support-reply"
                            type="textarea"
                            rows={3}
                            value={replyBody}
                            onChange={(event) => {
                              const value = event.target.value;
                              setReplyBody(value);
                              saveSupportReplyDraft(selected.id, value);
                            }}
                            onKeyDown={(event) => {
                              if (
                                (event.ctrlKey || event.metaKey) &&
                                event.key === "Enter"
                              ) {
                                event.preventDefault();
                                void handleReply();
                              }
                            }}
                          />
                          <p className="text-muted small mt-1 mb-0">
                            {t("support.replyShortcutHint")}
                          </p>
                          <Label for="support-reply-files" className="mt-2 mb-1">
                            {t("support.fields.attachment")}
                          </Label>
                          <Input
                            id="support-reply-files"
                            type="file"
                            multiple
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={(event) => {
                              const files = Array.from(event.target.files || []).slice(
                                0,
                                3
                              );
                              setReplyFiles(files);
                            }}
                          />
                          <p className="text-muted small mt-1 mb-0">
                            {t("support.fields.attachmentHint")}
                          </p>
                          <Button
                            color="primary"
                            className="mt-2"
                            disabled={replying || !replyBody.trim()}
                            onClick={() => void handleReply()}
                          >
                            {replying ? <Spinner size="sm" /> : t("support.actions.reply")}
                          </Button>
                        </div>
                      ) : (
                        <p className="text-muted small mb-0">
                          {t("support.replyOnlyWhenAwaiting")}
                        </p>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          ) : null}
        </Row>
      </Container>

      <SupportTicketModal
        isOpen={ticketModalOpen}
        toggle={() => setTicketModalOpen((open) => !open)}
        mode="ticket"
        onCreated={() => void loadList()}
      />
      <SupportTicketModal
        isOpen={feedbackModalOpen}
        toggle={() => setFeedbackModalOpen((open) => !open)}
        mode="feedback"
        onCreated={() => void loadList()}
      />
    </div>
  );
};

export default SupportTicketsPage;
